import { useState } from 'react'
import { Link } from 'react-router-dom'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Share2 } from 'lucide-react'

import CopyToClipboardInput from '@components/CopyToClipboardInput'
import { useTheme } from '@components/ThemeProvider'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import OverflowMenu from '@components/OverflowMenu'

const socialLogos = {
  linkedin: {
    light: '/linkedin-logo-black-outline.png',
    dark: '/linkedin-logo-white.png',
    alt: 'LinkedIn'
  },
  whatsapp: {
    light: '/whatsapp-logo-black-outline.png',
    dark: '/whatsapp-logo-white.png',
    alt: 'WhatsApp'
  },
  facebook: {
    light: '/facebook-logo-black-outline.png',
    dark: '/facebook-logo-white.png',
    alt: 'Facebook'
  },
  x: {
    light: '/x-logo-black-outline.png',
    dark: '/x-logo-white.png',
    alt: 'X logo'
  }
}

const SocialLogo = ({ type }) => {
  const { resolvedTheme } = useTheme()

  return (
    <Choose>
      <When condition={resolvedTheme === 'dark'}>
        <img
          src={socialLogos[type].dark}
          alt={socialLogos[type].alt}
          className="h-7 w-7 object-contain"
          loading="lazy"
        />
      </When>

      <Otherwise>
        <img
          src={socialLogos[type].light}
          alt={socialLogos[type].alt}
          className="h-7 w-7 object-contain"
        />
      </Otherwise>
    </Choose>
  )
}

const ShareDialog = ({ tutorId, name, visibility }) => {
  const [isOpen, setIsOpen] = useState(false)
  const tutorShareUrl = `${window.location.origin}/tutors/${tutorId}`
  const whatsappShareMessage = encodeURIComponent(`Check out my Mindjoy AI tutor: *${name}* \n\n${tutorShareUrl}`)
  const twitterShareMessage = encodeURIComponent(`Check out my @TeamMindjoy AI tutor: ${name}\n\n${tutorShareUrl}`)
  const facebookShareUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(tutorShareUrl)}`
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(tutorShareUrl)}`

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <OverflowMenu.Item
          onSelect={e => {
            e.preventDefault()
            setTimeout(() => setIsOpen(true), 0)
          }}
        >
          <div className='flex items-center text-md text-left whitespace-nowrap'>
            <Share2 className='h-4 w-4 mr-2' />
            Share
          </div>
        </OverflowMenu.Item>
      </DialogTrigger>

      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Share "{name}"</DialogTitle>
        </DialogHeader>

        <div className='space-y-5 py-2'>
          {visibility === 'PRIVATE' && (
            <Alert variant='default'>
              <InformationCircleIcon className='h-4 w-4' />
              <AlertTitle>Private Tutor</AlertTitle>
              <AlertDescription className='block'>
                {name} has visibility set to private.&nbsp;
                <DialogClose asChild>
                  <Link
                    to={`/educators/tutors/${tutorId}/settings`}
                    className='underline font-medium'
                  >
                    Update visibility
                  </Link>
                </DialogClose>
                &nbsp;to enable sharing via public link.
              </AlertDescription>
            </Alert>
          )}

          <CopyToClipboardInput
            value={tutorShareUrl}
            label='Public tutor link'
          />

          <div className='flex items-center my-5'>
            <div className='border-b flex-grow' />
            <span className='px-3 text-sm text-muted-foreground'>Or share via</span>
            <div className='border-b flex-grow' />
          </div>

          <div className='flex justify-center items-center gap-4'>
            <a
              href={linkedinShareUrl}
              target='_blank'
              rel='noreferrer'
              aria-label='Share on LinkedIn'
            >
              <Button variant='outline' className='rounded-full h-16 w-16 p-2'>
                <SocialLogo type="linkedin" />
              </Button>
            </a>
            <a
              href={`https://wa.me/?text=${whatsappShareMessage}`}
              target='_blank'
              rel='noreferrer'
              aria-label='Share on WhatsApp'
            >
              <Button variant='outline' className='rounded-full h-16 w-16 p-2'>
                <SocialLogo type="whatsapp" />
              </Button>
            </a>
            <a
              href={facebookShareUrl}
              target='_blank'
              rel='noreferrer'
              aria-label='Share on Facebook'
            >
              <Button variant='outline' className='rounded-full h-16 w-16 p-2'>
                <SocialLogo type="facebook" />
              </Button>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${twitterShareMessage}`}
              target='_blank'
              rel='noreferrer'
              aria-label='Share on X (formerly Twitter)'
            >
              <Button variant='outline' className='rounded-full h-16 w-16 p-2'>
                <SocialLogo type="x" />
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ShareDialog
